<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Metricas de Tempo',
  },
});

const firstResponse = computed(() => props.data?.first_response || {});
const resolution = computed(() => props.data?.resolution || {});

function formatTime(seconds) {
  if (!seconds || seconds === 0) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

function formatHours(hours) {
  if (!hours || hours === 0) return '0 h';
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-gauge text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- First Response Time Section -->
      <div class="p-4 rounded-lg bg-n-blue-3/30 border border-n-blue-5">
        <div class="flex items-center gap-2 mb-3">
          <span class="i-lucide-reply text-n-blue-10" />
          <span class="text-sm font-medium text-n-blue-11">Primeira Resposta</span>
        </div>

        <div class="grid grid-cols-3 gap-3 mb-3">
          <div class="text-center">
            <div class="text-2xl font-bold text-n-blue-11">
              {{ formatMinutes(firstResponse.average_minutes) }}
            </div>
            <div class="text-xs text-n-slate-11">Media</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-n-slate-12">
              {{ formatTime(firstResponse.median_seconds) }}
            </div>
            <div class="text-xs text-n-slate-11">Mediana</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-n-amber-11">
              {{ formatTime(firstResponse.p90_seconds) }}
            </div>
            <div class="text-xs text-n-slate-11">P90</div>
          </div>
        </div>

        <!-- Distribution bars -->
        <div class="grid grid-cols-5 gap-1 text-center">
          <div class="p-1.5 rounded bg-n-teal-3">
            <div class="text-sm font-bold text-n-teal-11">
              {{ firstResponse.under_1_min || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;1min</div>
          </div>
          <div class="p-1.5 rounded bg-n-teal-3/70">
            <div class="text-sm font-bold text-n-teal-11">
              {{ firstResponse.under_5_min || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;5min</div>
          </div>
          <div class="p-1.5 rounded bg-n-amber-3/70">
            <div class="text-sm font-bold text-n-amber-11">
              {{ firstResponse.under_15_min || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;15min</div>
          </div>
          <div class="p-1.5 rounded bg-n-amber-3">
            <div class="text-sm font-bold text-n-amber-11">
              {{ firstResponse.under_1_hour || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;1h</div>
          </div>
          <div class="p-1.5 rounded bg-n-ruby-3">
            <div class="text-sm font-bold text-n-ruby-11">
              {{ firstResponse.over_1_hour || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&gt;1h</div>
          </div>
        </div>
      </div>

      <!-- Resolution Time Section -->
      <div class="p-4 rounded-lg bg-n-teal-3/30 border border-n-teal-5">
        <div class="flex items-center gap-2 mb-3">
          <span class="i-lucide-check-circle text-n-teal-10" />
          <span class="text-sm font-medium text-n-teal-11">Tempo de Resolucao</span>
        </div>

        <div class="grid grid-cols-3 gap-3 mb-3">
          <div class="text-center">
            <div class="text-2xl font-bold text-n-teal-11">
              {{ formatHours(resolution.average_hours) }}
            </div>
            <div class="text-xs text-n-slate-11">Media</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-n-slate-12">
              {{ formatTime(resolution.median_seconds) }}
            </div>
            <div class="text-xs text-n-slate-11">Mediana</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-n-amber-11">
              {{ formatTime(resolution.p90_seconds) }}
            </div>
            <div class="text-xs text-n-slate-11">P90</div>
          </div>
        </div>

        <!-- Distribution bars -->
        <div class="grid grid-cols-4 gap-1 text-center">
          <div class="p-1.5 rounded bg-n-teal-3">
            <div class="text-sm font-bold text-n-teal-11">
              {{ resolution.under_1_hour || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;1h</div>
          </div>
          <div class="p-1.5 rounded bg-n-teal-3/70">
            <div class="text-sm font-bold text-n-teal-11">
              {{ resolution.under_4_hours || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;4h</div>
          </div>
          <div class="p-1.5 rounded bg-n-amber-3">
            <div class="text-sm font-bold text-n-amber-11">
              {{ resolution.under_24_hours || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&lt;24h</div>
          </div>
          <div class="p-1.5 rounded bg-n-ruby-3">
            <div class="text-sm font-bold text-n-ruby-11">
              {{ resolution.over_24_hours || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-11">&gt;24h</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
